import axios from 'axios'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import 'react-tabs/style/react-tabs.css'
import {fetchSalesOffices, headers} from '../../../../services/fetcher'
import {useAppDispatch, useAppSelector} from '../../../../redux/hooks'
import {
  filterSelector,
  resetFilterSalesOffices,
  setIsFetchData,
} from '../../../../redux/slices/filterSlices'
import {objToParams} from '../../../../utils/objToParams'
import Pagination from '../../component/Pagination'
import {ButtonGroups} from './ButtonGroups'
import {RowFilter} from './RowFilter'
import * as TableUI from '../../../../components/Table/Table'
import {useRowTable} from '../../../../hooks/useRowTable'
import {StyledCard} from '../../../../components/StyledCard'
import dateformat from 'src/utils/dateformat'

type Props = {
  className: string
}

const TableOffice: React.FC<Props> = ({className}) => {
  const {t} = useTranslation()
  const {filterSalesOffices} = useAppSelector(filterSelector)
  const dispatch = useAppDispatch()

  const handleReloadTable = () => {
    dispatch(resetFilterSalesOffices())
    onFetchSalesOffices()
  }

  const {RowEmptyData, RowErrorFetch, RowLoader} = useRowTable({
    colSpan: 5,
    onReloadTable: () => handleReloadTable(),
  })

  const [tooltip, setTooltip] = useState('Open')
  const [office, setOffice] = useState<any[]>([])
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(true)
  const [numberURL, setNumberURL] = useState([])
  const [isError, setIsError] = useState(false)

  const rowNumber = useMemo(() => {
    const per = filterSalesOffices.per_page ?? 5
    return page * per - per
  }, [filterSalesOffices.per_page, page])

  const onReloadTable = () => {
    dispatch(resetFilterSalesOffices())
  }

  const onFetchSalesOffices = useCallback(() => {
    setLoading(true)
    setIsError(false)
    fetchSalesOffices(filterSalesOffices)
      .then((res) => {
        const data = res.data.data
        setOffice(data.data)
        setPage(data.current_page)
        setNumberURL(data.links)
        setLoading(false)
      })
      .catch(() => setIsError(true))
      .finally(() => {
        setLoading(false)
        dispatch(setIsFetchData(false))
      })
  }, [filterSalesOffices])

  const paginateNumber = async (url: string) => {
    if (!url) return

    try {
      setLoading(true)

      const token = localStorage.getItem('token') as string
      const endpoint = `${url}&${objToParams(filterSalesOffices)}`
      const res = await axios.get(endpoint, {
        headers: {...headers, Authorization: `Bearer ${token}`},
      })
      const data = res.data.data

      setOffice(data.data)
      setPage(data.current_page)
      setNumberURL(data.links)
      setLoading(false)
    } catch (error) {
      setIsError(true)
    } finally {
      setLoading(false)
    }
  }

  const handleOpen = (e: any) => {
    const icon = e.target
    if (icon.classList.contains('iconArow')) {
      icon?.classList.remove('iconArow')
      icon?.classList.add('iconArowOpen')
      setTooltip('Close')
    } else {
      icon?.classList.remove('iconArowOpen')
      icon?.classList.add('iconArow')
      setTooltip('Open')
    }
    const target = e.currentTarget.getAttribute('data-target')
    const fold = document.getElementById(target)
    fold?.classList.toggle('fold-open')
  }

  useEffect(() => {
    const getData = setTimeout(onFetchSalesOffices, 1000)
    return () => clearTimeout(getData)
  }, [onFetchSalesOffices])

  useEffect(() => {
    return () => {
      onReloadTable()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <StyledCard>
      <ButtonGroups />

      <TableUI.TableWrapper>
        <TableUI.Table>
          <TableUI.THead>
            <tr>
              <th>{t('No')}</th>
              <th>{t('Sales Office Code')}</th>
              <th>{t('order-tanggal-dari')}</th>
              <th>{t('order-tanggal-berakhir')}</th>
              <th>{t('Controlling Area')}</th>
            </tr>
          </TableUI.THead>

          <TableUI.TBody>
            <RowFilter />

            {loading ? (
              <RowLoader />
            ) : isError ? (
              <RowErrorFetch />
            ) : office.length === 0 ? (
              <RowEmptyData />
            ) : (
              office?.map((offices, _index) => (
                <>
                  <tr>
                    <td>
                      <div className='d-flex justify-content-center align-items-center p-0'>
                        <button
                          className='iconArow d-flex justify-content-center align-items-center me-2'
                          data-target={'target' + (_index + 1) + rowNumber}
                          title={t(tooltip)}
                          onClick={handleOpen}
                        ></button>
                        <p>{_index + 1 + rowNumber}</p>
                      </div>
                    </td>
                    <td>{offices?.sales_office}</td>
                    <td>{dateformat(offices?.from_date)}</td>
                    <td>{dateformat(offices?.to_date)}</td>
                    <td>{offices?.controlling_area}</td>
                  </tr>

                  <tr className='fold' id={'target' + (_index + 1) + rowNumber}>
                    <TableUI.TableDetail colSpan={5}>
                      <TableUI.TableWrapper style={{marginBottom: 0}}>
                        <TableUI.Table>
                          <TableUI.THead>
                            <tr style={{backgroundColor: '#274070'}}>
                              <th>Id</th>
                              <th>Sales Org</th>
                              <th>{t('Company Name')}</th>
                              <th>{t('Distribution Channel')}</th>
                              <th>{t('DC Description')}</th>
                              <th>{t('Division')}</th>
                              <th>{t('Division Desc')}</th>
                              <th>{t('Sales Office Code')}</th>
                              <th>{t('Description')}</th>
                            </tr>
                          </TableUI.THead>
                          <TableUI.TBody>
                            {offices?.sa_assignments?.map((subItem: any, subIdx: any) => (
                              <tr key={subIdx}>
                                <td>{subItem.id}</td>
                                <td>{subItem.sales_org}</td>
                                <td>{subItem.company_name}</td>
                                <td>{subItem.distribution_channel}</td>
                                <td>{subItem.division}</td>
                                <td>{subItem.dc_description}</td>
                                <td>{subItem.division_desc}</td>
                                <td>{subItem.sales_office}</td>
                                <td>{subItem.description}</td>
                              </tr>
                            ))}
                          </TableUI.TBody>
                        </TableUI.Table>
                      </TableUI.TableWrapper>
                    </TableUI.TableDetail>
                  </tr>
                </>
              ))
            )}
          </TableUI.TBody>
        </TableUI.Table>
      </TableUI.TableWrapper>

      <Pagination paginate={paginateNumber} test={numberURL} />
    </StyledCard>
  )
}

export {TableOffice}
